import React from 'react';
import { motion } from 'framer-motion';
import { Database, Globe, Zap } from 'lucide-react';

const Projects: React.FC = () => {
  const projects = [
    {
      title: 'Order Management System (OMS)',
      company: 'Rebelfoods Private Ltd.',
      description: 'Enterprise-scale order management system handling high-volume food delivery orders with real-time kitchen display integration.',
      features: [
        'Handles order acceptance and kitchen display system integration',
        'Real-time order status updates to Swiggy, Zomato channels',
        'High-performance API with sub-200ms response times',
        'Comprehensive monitoring with DataDog integration',
        'Microservices architecture with seamless backward compatibility'
      ],
      technologies: ['Java 17', 'Spring Boot', 'MySQL', 'Kafka', 'DataDog', 'REST APIs'],
      impact: 'Improved scalability by 300% and reduced latency by 40%',
      icon: Database,
      category: 'Enterprise System'
    },
    {
      title: 'Catalogue Management System (CMS)',
      company: 'Rebelfoods Private Ltd.',
      description: 'Comprehensive menu and product management system supporting multiple delivery channels and store operations.',
      features: [
        'Menu creation and maintenance for multiple channels',
        'Product categorization and pricing management',
        'Store and delivery polygon management',
        'Multi-channel integration (Swiggy, Zomato, Eatsure)',
        'Real-time catalogue synchronization'
      ],
      technologies: ['Java 17', 'Spring Framework', 'MongoDB', 'Redis', 'JPA', 'Microservices'],
      impact: 'Streamlined catalogue operations across 100+ stores',
      icon: Globe,
      category: 'Management System'
    },
    {
      title: 'SPARK Inventory System',
      company: 'Rebelfoods Private Ltd.',
      description: 'Real-time inventory management system providing near-real-time stock tracking and lifecycle management.',
      features: [
        'Real-time inventory tracking and updates',
        'Order lifecycle-based inventory management',
        'Near-real-time stock reflection',
        'Automated inventory adjustments',
        'Comprehensive audit trails'
      ],
      technologies: ['Java 17', 'Spring Boot', 'MySQL', 'RabbitMQ', 'Redis', 'ELK Stack'],
      impact: 'Achieved near-real-time inventory accuracy with zero data loss',
      icon: Zap,
      category: 'Inventory System'
    },
    {
      title: 'Financial Services Platform',
      company: 'Indiabulls Consumer Finance Ltd.',
      description: 'Comprehensive financial services platform integrating multiple third-party payment and booking systems.',
      features: [
        'Third-party platform integration (Cleartrip, RedBus, Euronet)',
        'Payment processing and transaction management',
        'Multi-channel service portfolio',
        'Real-time transaction monitoring',
        'Comprehensive reporting and analytics'
      ],
      technologies: ['Java', 'Spring Framework', 'Oracle', 'REST APIs', 'PL/SQL', 'Jira'],
      impact: 'Enhanced service portfolio and delivered projects 15% ahead of schedule',
      icon: Database,
      category: 'Financial Platform'
    },
    {
      title: 'Enterprise Migration Project',
      company: 'Accenture India Pvt. Ltd.',
      description: 'Large-scale Oracle-to-SAP migration project affecting 50,000+ users with zero downtime.',
      features: [
        'Oracle-to-SAP migration for 50,000+ users',
        'Zero downtime deployment strategy',
        'Data migration and validation',
        'User training and support',
        'Comprehensive testing and rollback plans'
      ],
      technologies: ['Oracle', 'SAP', 'PL/SQL', 'Java', 'Database Migration', 'Testing'],
      impact: 'Successfully migrated 50,000+ users with 100% SLA compliance',
      icon: Zap,
      category: 'Migration Project'
    }
  ];

  return (
    <section id="projects" className="section-padding">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Featured <span className="gradient-text">Projects</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Key projects and systems I've architected and delivered, showcasing expertise in 
            enterprise-scale applications and digital transformation.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="card p-6 hover:shadow-xl transition-shadow duration-300"
            >
              {/* Project Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                    <project.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {project.title}
                    </h3>
                    <p className="text-sm text-primary-600 dark:text-primary-400 font-medium">
                      {project.company}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {project.description}
              </p>

              {/* Key Features */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Key Features:
                </h4>
                <ul className="space-y-2">
                  {project.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2 mt-2 flex-shrink-0"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Technologies */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Technologies Used:
                </h4>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-3 py-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Impact */}
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <h4 className="font-semibold text-green-800 dark:text-green-300 mb-1">
                  Impact & Results:
                </h4>
                <p className="text-sm text-green-700 dark:text-green-400">
                  {project.impact}
                </p>
              </div>
            </motion.div>
          ))}
        </div>


      </div>
    </section>
  );
};

export default Projects;
